
import { defineComponent, ref, computed, onMounted, nextTick } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";
import ToolTitle from "@/components/SideToolBar/Tools/ToolTitle.vue";
import {
  currentText,
  currentFont,
  currentFontSize,
  makeNewText,
  updateText,
  pxToPts,
  ptsToPx,
} from "@/canvas-api/UserObjects/Text";
import usePickr from "@/composables/usePickr";
import { ColorPickrState } from "@/types";
import fontData from "@/data/fonts.json";
import { onBoardingTextToolRef } from "@/onboarding/Refs";
import { opacity, strokeWidth, fillElement, borderElement } from "@/canvas-api/EventListeners"

// prettier-ignore
const fontSizesData = [6.667, 7.333, 8.667, 10, 10.667, 12, 13.333, 14, 14.667, 16, 18.667, 21.333, 24, 26.667, 29.333, 32, 34.667, 37.333, 48, 64, 96];

export default defineComponent({
  name: "TexTool",
  components: {
    ToolTitle,
  },
  setup() {
    const title = ref("");

    const showFonts = ref(false);
    const formattedFonts = ref<string[]>(Object.values(fontData).map((font) => font.split(":")[0]));
    const filteredFonts = computed(() =>
      formattedFonts.value.filter((font) => font !== currentFont.value)
    );

    const showSizes = ref(false);
    const fontSizes = ref<number[]>(fontSizesData);
    const filteredFontSizes = computed(() =>
      fontSizes.value.filter((size) => size !== currentFontSize.value)
    );

    const handleFontSize = (e: { target: HTMLInputElement }) => {
      const value = Number(e.target.value);

      if (!Number.isNaN(value)) {
        currentFontSize.value = ptsToPx(value);

        updateText("fontSize", currentFontSize.value);
      }
    };


    const currentActiveObject = computed(() => store.state.currentActiveObject as fabric.IText);
    const colorPickrState = computed<ColorPickrState>(() => store.getters.colorPickrState);

    const hasBold = ref(false);
    const hasItalic = ref(false);
    const hasBoldItalic = ref(false);
    const checkFontAttrs = async (fonts: any, currentlySelectedFont: string) => {
      hasBold.value = false;
      hasItalic.value = false;
      hasBoldItalic.value = false;

      const fontValue = fonts[currentlySelectedFont] ? fonts[currentlySelectedFont].split(":") : [];

      if (!fontValue[1]) {
        let weightInput = document.getElementById("fontWeight") as HTMLInputElement;
        weightInput.checked = false;

        let styleInput = document.getElementById("fontStyle") as HTMLInputElement;
        styleInput.checked = false;

        //Throw in some await nextTicks because if we switch to a fontFamily that does not have
        //Bold or Italic and we overload the watcher with too many changes at once
        //The watcher in Canvas.vue only sees one change
        await nextTick();
        updateText("fontWeight", "normal");
        await nextTick();
        updateText("fontStyle", "normal");
        return;
      }

      const fontAttrs = fontValue[1].split(",");

      fontAttrs.forEach((attr: string) => {
        if (attr.includes("700")) {
          hasBold.value = true;
        }

        if (attr.includes("400i")) {
          hasItalic.value = true;
        }

        if (attr.includes("700i")) {
          hasBoldItalic.value = true;
        }
      });

      if (!hasBold.value) {
        let weightInput = document.getElementById("fontWeight") as HTMLInputElement;
        weightInput.checked = false;

        await nextTick();
        updateText("fontWeight", "normal");
      }

      if (!hasItalic.value) {
        let styleInput = document.getElementById("fontStyle") as HTMLInputElement;
        styleInput.checked = false;

        await nextTick();
        updateText("fontStyle", "normal");
      }
    };

    onMounted(() => {
      const defaultSize = 24;

      if (!currentActiveObject.value || currentActiveObject.value.name !== "iText") {
        const defaultText = "Enter Text Here";
        const defaultFont = "Open Sans";

        currentText.value = defaultText;
        currentFont.value = defaultFont;
        currentFontSize.value = defaultSize;

        makeNewText(currentText.value, currentFontSize.value, currentFont.value);

        if (borderElement.value) {
          borderElement.value.style.background = "#0001ba";
        }

        if (fillElement.value) {
          fillElement.value.style.background = "#000";
        }

        opacity.value = 0;
        strokeWidth.value = 0
      } else if (currentActiveObject.value.name === "iText") {
        let activeObj = currentActiveObject.value as fabric.IText;

        currentText.value = activeObj.text!;
        currentFont.value = activeObj.fontFamily!;
        currentFontSize.value = activeObj.fontSize! * activeObj.scaleX!;

        if (activeObj.fontWeight === "bold") {
          let input = document.getElementById("fontWeight") as HTMLInputElement;
          input.checked = true;
        }

        if (activeObj.fontStyle === "italic") {
          let input = document.getElementById("fontStyle") as HTMLInputElement;
          input.checked = true;
        }

        if (activeObj.underline) {
          let input = document.getElementById("underline") as HTMLInputElement;
          input.checked = true;
        }

        let textAlignInput = document.getElementById(
          `${activeObj.textAlign}Align`
        ) as HTMLInputElement;

        if (textAlignInput) {
          textAlignInput.checked = true;
        }

        if (borderElement.value) {
          borderElement.value.style.background = activeObj.stroke as string;
        }

        if (fillElement.value) {
          fillElement.value.style.background = activeObj.fill as string;
        }

        opacity.value = 100 - activeObj.opacity! * 100;
        strokeWidth.value = activeObj.strokeWidth! * 10;
      }

      checkFontAttrs(fontData, currentFont.value);

      //Triggering rerender on mount, if no rerender trigger, canvas doesn"t get new Itext Object
      title.value = "Text";
    });

    const setTextProp = (
      e: { target: HTMLInputElement },
      prop: string,
      value?: string | number
    ) => {
      let checked = e.target.checked;

      switch (prop) {
        case "fontFamily": {
          currentFont.value = value as string;
          updateText(prop, currentFont.value);
          checkFontAttrs(fontData, currentFont.value);
          break;
        }
        case "fontSize": {
          currentFontSize.value = value as number;

          updateText(prop, currentFontSize.value);
          break;
        }
        case "fontWeight": {
          if (checked) updateText(prop, "bold");
          else updateText(prop, "normal");

          break;
        }
        case "fontStyle": {
          if (checked) updateText(prop, "italic");
          else updateText(prop, "normal");

          break;
        }
        case "underline": {
          if (checked) updateText(prop, true);
          else updateText(prop, false);

          break;
        }
        case "textAlign": {
          updateText(prop, value as string);

          break;
        }
        case "strokeWidth": {
          let inputValue = Number(e.target.value);

          strokeWidth.value = inputValue;

          updateText(prop, strokeWidth.value / 10);
          break;
        }
        case "opacity": {
          let inputValue = Number(e.target.value);

          opacity.value = inputValue;

          updateText(prop, 1 - opacity.value / 100);
          break;
        }
        case "text": {
          updateText(prop, currentText.value);

          break;
        }
      }
    };

    const handleToggleColorPickr = (event: { target: HTMLDivElement }): void => {
      const TARGET = event.target;

      switch (TARGET.getAttribute("id")) {
        case "fill-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "fill",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        case "border-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "stroke",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        default: {
          throw new Error("Missing target id!, Customize.vue");
        }
      }
    };

    return {
      onBoardingTextToolRef,
      pxToPts,
      handleFontSize,
      currentText,
      currentFont,
      currentFontSize,
      title,
      showFonts,
      formattedFonts,
      filteredFonts,
      showSizes,
      fontSizes,
      filteredFontSizes,
      // fontSizeInput,
      fillElement,
      borderElement,
      opacity,
      strokeWidth,
      hasBold,
      hasItalic,
      hasBoldItalic,
      setTextProp,
      handleToggleColorPickr,
    };
  },
});
